import { Box, GlobalStyles } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { ReactNode, useEffect, useMemo } from 'react'
import { Toaster } from 'react-hot-toast'
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import Header from './components/layout/Header'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeContext'
import InviteLanding from './pages/auth/InviteLanding'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import BookEditor from './pages/BookEditor'
import CharacterPage from './pages/CharacterPage'
import Dashboard from './pages/Dashboard'
import Graph from './pages/Graph'
import PeoplePage from './pages/PeoplePage'
import TheLastManifesto from './pages/TheLastManifesto'
import { createAppTheme } from './styles/theme'
import { resolveTokens } from './styles/themeTokens'
import { logger } from './utils/logger'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

function AppContent() {
  const location = useLocation()
  const navigate = useNavigate()
  const isRootPath = location.pathname === '/'
  const isLoginPath = location.pathname === '/login'
  // Editor routes handle their own header (Google Docs style)
  // Routes that draw their own chrome: the editor, the reading page under /read/, and
  // the invitation landing — which is the reader's first page of the book, and must not
  // wear the app's header bar above it.
  const drawsOwnChrome =
    location.pathname.startsWith('/edit/') ||
    location.pathname.startsWith('/read/') ||
    location.pathname.startsWith('/invite/') ||
    location.pathname.startsWith('/character/')
  const APP_LAUNCH_BOOK_ID_KEY = 'manifesto.lastStandaloneBookId'
  const APP_LAUNCH_CHECK_KEY = 'manifesto.standaloneLaunchChecked'

  useEffect(() => {
    const editMatch = location.pathname.match(/^\/edit\/([^/]+)/)
    if (!editMatch) {
      return
    }

    localStorage.setItem(APP_LAUNCH_BOOK_ID_KEY, editMatch[1])
  }, [location.pathname])

  useEffect(() => {
    if (sessionStorage.getItem(APP_LAUNCH_CHECK_KEY) === '1') {
      return
    }
    sessionStorage.setItem(APP_LAUNCH_CHECK_KEY, '1')

    const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean }
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true

    if (!isStandalone || !isRootPath) {
      return
    }

    const lastBookId = localStorage.getItem(APP_LAUNCH_BOOK_ID_KEY)
    if (!lastBookId) {
      return
    }

    navigate(`/edit/${lastBookId}/רשומון`, { replace: true })
  }, [isRootPath, navigate])

  // Global keyboard shortcut: cmd+opt+shift+c to copy logger output
  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      // cmd+opt+shift+c (Mac) or ctrl+alt+shift+c (Windows/Linux)
      // Use event.code instead of event.key because option+shift+c on Mac produces 'Ç'
      if ((event.metaKey || event.ctrlKey) && event.altKey && event.shiftKey && event.code === 'KeyC') {
        event.preventDefault()
        try {
          const logs = logger.getAllLogs()
          await navigator.clipboard.writeText(logs)
        } catch (error) {
          logger.error('Failed to copy logger output:', error)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // The root path, login, the editor and the reading page all handle their own header.
  const showDefaultHeader = !isRootPath && !isLoginPath && !drawsOwnChrome

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      {showDefaultHeader && <Header />}
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route
            path="/"
            element={
              <RequireAuth>
                <TheLastManifesto />
              </RequireAuth>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route
            path="/register"
            element={
              <RequireInvite>
                <Register />
              </RequireInvite>
            }
          />
          <Route path="/invite/:token" element={<InviteLanding />} />
          <Route path="/character/:bookId/:tagId" element={<CharacterPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/invitations" element={<Navigate to="/people" replace />} />

          {/* Reader routes — registered users only. The same reading page as `/`, which
              is the point: there is one reader, and it is tailored to whoever is holding
              it. These carry a book, and optionally the entry to open on. */}
          <Route
            path="/read/:bookId"
            element={
              <RequireAuth>
                <TheLastManifesto />
              </RequireAuth>
            }
          />
          <Route
            path="/read/:bookId/:chapterId"
            element={
              <RequireAuth>
                <TheLastManifesto />
              </RequireAuth>
            }
          />

          {/* Author routes (require auth) */}
          <Route path="/edit/:bookId" element={<BookEditor />} />
          <Route path="/edit/:bookId/:chapterId" element={<BookEditor />} />
          <Route path="/graph" element={<Graph />} />
        </Routes>
      </Box>
    </Box>
  )
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) {
    return null
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function RequireInvite({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams()
  if (!searchParams.get('invitation')) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

/** Injects CSS custom properties from themeTokens onto :root */
function ThemeVariables() {
  const { resolvedMode } = useThemeMode()
  const tokens = useMemo(() => resolveTokens(resolvedMode), [resolvedMode])
  const cssVars = useMemo(() => {
    const vars: Record<string, string> = {}
    for (const [key, val] of Object.entries(tokens)) {
      vars[key] = val
    }
    return {
      ':root': vars,
      'html, body, #root': {
        scrollbarColor: 'var(--scrollbar-thumb) var(--scrollbar-track)',
        scrollbarWidth: 'thin',
      },
      '*': {
        scrollbarColor: 'var(--scrollbar-thumb) var(--scrollbar-track)',
        scrollbarWidth: 'thin',
      },
      '*::-webkit-scrollbar': {
        width: 10,
        height: 10,
      },
      '*::-webkit-scrollbar-track': {
        background: 'var(--scrollbar-track)',
      },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: 'var(--scrollbar-thumb)',
        borderRadius: 999,
        border: '2px solid var(--scrollbar-track)',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'var(--scrollbar-thumb-hover)',
      },
      '*::-webkit-scrollbar-corner': {
        background: 'var(--scrollbar-track)',
      },
    }
  }, [tokens])

  return <GlobalStyles styles={cssVars} />
}

function ThemedApp() {
  const { resolvedMode } = useThemeMode()
  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ThemeVariables />
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID} locale="en">
        <AuthProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AppContent />
          </Router>
          <Toaster
            position="top-right"
            gutter={10}
            containerStyle={{
              top: 'max(16px, env(safe-area-inset-top))',
              right: 'max(16px, env(safe-area-inset-right))',
            }}
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 14,
                boxShadow: 'var(--floating-panel-shadow)',
                padding: '14px 18px',
                fontSize: '0.875rem',
                fontWeight: 500,
                letterSpacing: '0.01em',
                maxWidth: 'min(420px, calc(100vw - 32px))',
              },
              success: {
                duration: 3200,
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#ffffff',
                },
              },
              error: {
                duration: 5200,
                iconTheme: {
                  primary: '#f87171',
                  secondary: '#ffffff',
                },
              },
              loading: {
                iconTheme: {
                  primary: '#6366f1',
                  secondary: '#e0e7ff',
                },
              },
            }}
          />
        </AuthProvider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  )
}

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  )
}

export default App
